/**
 * **Connections → Automation connections** (`specs/M6-A4-integrations.md` §7).
 *
 * Built from the measured page, not from a squint at the screenshot:
 * `discovery/ui-reference/admin/connections--automation-connections.json` (captured 2026-09-11
 * by `tools/browser/capture-connections.mjs`, read-only) gives the table at **(352, 258),
 * 1048 wide**, **row height 40**, header row 40, and the column order and widths
 * *⋯ 64 · App 153 · Status 200 · Connection 133 · Owner 80 · Boards 133 · Automations 160 ·
 * Updated 124*, with the search input at **(393, 207), 474 wide**. The same numbers are in
 * `admin.css` as `--ez-connections-*`.
 *
 * Three deliberate differences from monday, all of them consequences of what this product is:
 *
 * 1. monday's rows are **app installations** (14 of them on this account, mostly AI marketplace
 *    apps, `discovery/admin-settings.md` §Connections). Ours are the four apps the automation
 *    catalogue can actually use, listed whether or not they are connected — a row that says
 *    "Not connected" with a **Connect** button is the whole point of the page.
 * 2. The **Owner** and **Boards** columns are monday's per-installation metadata; ours are one
 *    connection per account, so Owner is who connected it and Boards is absent. The column is
 *    kept in the header order and rendered empty rather than dropped, so the table still lines
 *    up with the capture.
 * 3. A credential is **write-only**. The page can show `•••• 4f2a`, never the value: nothing
 *    in the tRPC surface returns it (`apps/api/src/services/connections.ts` rule 1).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfirm } from '@elizmonday/ui';
import type { ConnectionApp, ConnectionRow, ConnectionsSource } from './types';

export const CONNECTION_APPS: ReadonlyArray<{ app: ConnectionApp; name: string; blurb: string; secretLabel: string | null }> = [
  {
    app: 'slack',
    name: 'Slack',
    blurb: 'Posts to a channel or a person with chat.postMessage.',
    secretLabel: 'Bot user OAuth token (xoxb-…)',
  },
  {
    app: 'gmail',
    name: 'Gmail',
    blurb: 'Sends email as the connected Google account.',
    secretLabel: 'Google OAuth client + refresh token (JSON)',
  },
  {
    app: 'googleCalendar',
    name: 'Google Calendar',
    blurb: 'Creates events on the connected account’s primary calendar.',
    secretLabel: 'Google OAuth client + refresh token (JSON)',
  },
  {
    app: 'webhooks',
    name: 'Outgoing webhooks',
    blurb: 'The hosts an automation webhook may POST to. No credential is stored.',
    secretLabel: null,
  },
];

const STATUS_TEXT: Record<string, string> = {
  connected: 'Connected',
  not_connected: 'Not connected',
  expired: 'Expired',
  revoked: 'Revoked',
};

const ago = (iso: string | null): string => {
  if (iso === null) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${String(days)} days ago`;
  if (days < 365) return `${String(Math.floor(days / 30))} months ago`;
  return days < 730 ? 'last year' : `${String(Math.floor(days / 365))} years ago`;
};

export function ConnectionsScreen({ source }: { source: ConnectionsSource }): React.ReactElement {
  const [rows, setRows] = useState<ConnectionRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ConnectionApp | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    source
      .list()
      .then(setRows)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)));
  }, [source]);

  useEffect(reload, [reload]);

  const byApp = useMemo(() => new Map((rows ?? []).map((row) => [row.app, row])), [rows]);
  const visible = useMemo(
    () =>
      CONNECTION_APPS.filter((entry) =>
        search.trim().length === 0 ? true : entry.name.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [search],
  );

  return (
    <div data-testid="admin-connections">
      <h1 className="ez-admin__heading">Automation connections</h1>
      <p className="ez-admin__subheading">
        The account-level credentials the automation catalogue runs through. An automation whose app is
        not connected stays switched off and says so, rather than failing on every run.
      </p>

      <div className="ez-connections__toolbar">
        <input
          className="ez-connections__search"
          type="search"
          placeholder="Search"
          aria-label="Search connections"
          data-testid="connections-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {error === null ? null : (
        <div className="ez-admin__error" role="alert" data-testid="connections-error">
          {error}
        </div>
      )}

      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table ez-connections__table" data-testid="connections-table">
          <thead>
            <tr>
              <th className="ez-connections__col-menu" />
              <th className="ez-connections__col-app">App</th>
              <th className="ez-connections__col-status">Status</th>
              <th className="ez-connections__col-connection">Connection</th>
              <th className="ez-connections__col-owner">Owner</th>
              <th className="ez-connections__col-boards">Boards</th>
              <th className="ez-connections__col-automations">Automations</th>
              <th className="ez-connections__col-updated">Updated</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((entry) => {
              const row = byApp.get(entry.app);
              const status = row?.status ?? 'not_connected';
              return (
                <tr key={entry.app} data-testid={`connection-row-${entry.app}`} data-status={status}>
                  <td className="ez-connections__col-menu">
                    <button
                      type="button"
                      className="ez-connections__menu"
                      aria-label={`Configure ${entry.name}`}
                      data-testid={`connection-configure-${entry.app}`}
                      onClick={() => setEditing(entry.app)}
                    >
                      ⋯
                    </button>
                  </td>
                  <td className="ez-connections__col-app">{entry.name}</td>
                  <td className="ez-connections__col-status">
                    <span className="ez-admin-chip" data-status={status}>
                      {STATUS_TEXT[status] ?? status}
                    </span>
                  </td>
                  <td className="ez-connections__col-connection" title={entry.blurb}>
                    {row?.label ?? (row?.secretHint === null || row?.secretHint === undefined ? '—' : `•••• ${row.secretHint}`)}
                  </td>
                  <td className="ez-connections__col-owner" />
                  <td className="ez-connections__col-boards" />
                  <td className="ez-connections__col-automations">
                    {typeof row?.metadata.automationCount === 'number' ? String(row.metadata.automationCount) : ''}
                  </td>
                  <td className="ez-connections__col-updated">{ago(row?.updatedAt ?? null)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing === null ? null : (
        <ConnectionDialog
          app={editing}
          row={byApp.get(editing) ?? null}
          source={source}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * The connect dialog. One text field for the credential, which is **write-only** — reopening
 * the dialog on a connected app shows the hint, never the value, and leaving the field empty
 * keeps the stored credential untouched.
 */
export function ConnectionDialog({
  app,
  row,
  source,
  onClose,
  onSaved,
}: {
  app: ConnectionApp;
  row: ConnectionRow | null;
  source: ConnectionsSource;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const entry = CONNECTION_APPS.find((candidate) => candidate.app === app) as (typeof CONNECTION_APPS)[number];
  const [label, setLabel] = useState(row?.label ?? '');
  const [secret, setSecret] = useState('');
  const [hosts, setHosts] = useState(
    Array.isArray(row?.metadata.allowedHosts) ? (row.metadata.allowedHosts as string[]).join('\n') : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * P-012: **Disconnect** destroyed the stored credential on the first click. It cannot be
   * undone — the secret is write-only (rule 3 at the top of this file), so re-connecting means
   * fetching a new token from the app — and every automation using it stops meanwhile.
   */
  const confirm = useConfirm();

  const submit = (): void => {
    setBusy(true);
    setError(null);
    const metadata: Record<string, unknown> =
      app === 'webhooks'
        ? {
            ...(row?.metadata ?? {}),
            allowedHosts: hosts
              .split(/[\n,]/)
              .map((host) => host.trim())
              .filter((host) => host.length > 0),
          }
        : (row?.metadata ?? {});
    source
      .save({ app, label: label.length === 0 ? null : label, metadata, ...(secret.length === 0 ? {} : { secret }) })
      .then(onSaved)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
        setBusy(false);
      });
  };

  return (
    <div className="ez-dialog-backdrop" data-testid="connection-dialog-backdrop">
      <div className="ez-connections__dialog" role="dialog" aria-label={`Connect ${entry.name}`} data-testid="connection-dialog">
        <h2 className="ez-connections__dialog-title">{entry.name}</h2>
        <p className="ez-connections__dialog-blurb">{entry.blurb}</p>

        <label className="ez-connections__field">
          <span>Name</span>
          <input
            value={label}
            data-testid="connection-label"
            placeholder={app === 'slack' ? 'Eli Z Group workspace' : 'roberto@elizetaplus.com'}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>

        {entry.secretLabel === null ? (
          <label className="ez-connections__field">
            <span>Allowed hosts</span>
            <textarea
              rows={4}
              value={hosts}
              data-testid="connection-hosts"
              placeholder={'hooks.example.com\n.partner.example'}
              onChange={(event) => setHosts(event.target.value)}
            />
            <small>
              One host per line. A leading dot matches subdomains. Anything not listed is refused before
              the request is made, and a host that resolves to a private address is always refused.
            </small>
          </label>
        ) : (
          <label className="ez-connections__field">
            <span>{entry.secretLabel}</span>
            <input
              type="password"
              value={secret}
              data-testid="connection-secret"
              autoComplete="off"
              placeholder={row?.secretHint === null || row?.secretHint === undefined ? '' : `•••• ${row.secretHint} — leave empty to keep`}
              onChange={(event) => setSecret(event.target.value)}
            />
            <small>Stored encrypted with TOKEN_ENCRYPTION_KEY and never shown again.</small>
          </label>
        )}

        {error === null ? null : (
          <div className="ez-admin__error" role="alert" data-testid="connection-dialog-error">
            {error}
          </div>
        )}

        <div className="ez-connections__dialog-actions">
          {row !== null && row.id.length > 0 ? (
            <button
              type="button"
              className="ez-connections__danger"
              data-testid="connection-disconnect"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  const ok = await confirm({
                    title: `Disconnect ${entry.name}?`,
                    body: 'Automations and integrations that use this connection stop running until it is connected again. The stored credential is deleted and cannot be recovered.',
                    confirmLabel: 'Disconnect',
                    danger: true,
                  });
                  if (!ok) return;
                  setBusy(true);
                  try {
                    await source.disconnect({ app });
                    onSaved();
                  } catch (cause: unknown) {
                    setError(cause instanceof Error ? cause.message : String(cause));
                    setBusy(false);
                  }
                })();
              }}
            >
              Disconnect
            </button>
          ) : (
            <span />
          )}
          <div>
            <button type="button" data-testid="connection-cancel" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="ez-connections__primary" data-testid="connection-save" onClick={submit} disabled={busy}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
